import React, { useState } from 'react';
import {
  Button,
  Card,
  Alert,
  Input,
  Modal,
  PageHeading,
  Icon,
  Pill,
  Eyebrow,
} from '../../components/ui';
import {
  FiSearch,
  FiPlus,
  FiTrash2,
  FiArrowRight,
  FiCheck,
  FiAlertTriangle,
  FiTrendingUp,
  FiHeart,
  FiZap,
  FiTarget,
  FiAward,
  FiClock,
  FiUser,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Internal showcase route at `/dev/components`. Mirrors the design package's
 * styles.css 1:1, using both:
 *   - React primitives from src/components/ui (preferred for new code)
 *   - The design's raw CSS classes (.btn, .card, .pill) — verifies parity
 *     and is the form most screen-level component code will use as we
 *     migrate them.
 *
 * Public route, no auth, safe in production.
 */
export default function ComponentsShowcase() {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showInfo, setShowInfo] = useState(true);
  const { theme, resolvedTheme, cycleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-canvas text-fg px-6 py-10 font-sans">
      <div className="max-w-5xl mx-auto">
        <PageHeading
          hello="Design system"
          title="Phase 1 primitives"
          subtitle="Tokens, primitives, and the design's raw CSS recipes (.btn, .card, .pill) all live here. Toggle the theme in the top nav to verify dark mode."
          actions={
            <Button variant="ghost" size="sm" onClick={cycleTheme}>
              Theme: {theme} ({resolvedTheme})
            </Button>
          }
        />

        <Section title="Button" description="React primitive — variants, sizes, loading, icons.">
          <div className="flex flex-wrap gap-3 items-center">
            <Button>Primary</Button>
            <Button variant="gradient">Gradient</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive" leadingIcon={<FiTrash2 />}>
              Delete
            </Button>
            <Button loading>Saving…</Button>
            <Button disabled>Disabled</Button>
          </div>
          <div className="flex flex-wrap gap-3 items-center mt-4">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
            <Button trailingIcon={<FiArrowRight />}>Continue</Button>
            <Button leadingIcon={<FiPlus />} variant="secondary">
              Add new
            </Button>
          </div>
          <Eyebrow className="mt-6 block">Raw class form (.btn .btn-primary)</Eyebrow>
          <div className="flex flex-wrap gap-3 items-center mt-2">
            <button className="btn btn-primary">Primary</button>
            <button className="btn btn-grad">Gradient</button>
            <button className="btn btn-secondary">Secondary</button>
            <button className="btn btn-ghost">Ghost</button>
            <button className="btn btn-primary btn-sm">Small primary</button>
          </div>
        </Section>

        <Section title="Card" description="Surface anchor — bg-surface + 1px border + shadow-card.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px]">
            <Card>
              <p className="font-semibold">Default card</p>
              <p className="text-sm text-fg-muted mt-1">
                Padding: md (22px), radius: 16px.
              </p>
            </Card>
            <Card padding="lg">
              <p className="font-semibold">Larger card</p>
              <p className="text-sm text-fg-muted mt-1">
                Padding: lg (28px).
              </p>
            </Card>
            <Card glow interactive>
              <p className="font-semibold">Glow + interactive</p>
              <p className="text-sm text-fg-muted mt-1">
                Primary-tinted gradient surface, hover lifts shadow.
              </p>
            </Card>
          </div>
          <Eyebrow className="mt-6 block">Raw class form (.card.pad)</Eyebrow>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] mt-2">
            <div className="card pad">
              <p className="font-semibold">.card.pad</p>
            </div>
            <div className="card pad-lg">
              <p className="font-semibold">.card.pad-lg</p>
            </div>
            <div className="card glow pad">
              <p className="font-semibold">.card.glow.pad</p>
            </div>
          </div>
        </Section>

        <Section title="Eyebrow" description="Small uppercase tracked label above headings.">
          <div className="space-y-4">
            <div>
              <Eyebrow>Roadmap</Eyebrow>
              <h3 className="text-xl font-semibold text-fg mt-1">
                What's coming next
              </h3>
            </div>
            <div>
              <Eyebrow tone="primary" leadingIcon={FiZap}>
                New
              </Eyebrow>
              <h3 className="text-xl font-semibold text-fg mt-1">
                Custom assessments
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Eyebrow tone="success">Live</Eyebrow>
              <Eyebrow tone="warning">Beta</Eyebrow>
              <Eyebrow tone="error">Deprecated</Eyebrow>
              <Eyebrow tone="brand">Coach</Eyebrow>
              <Eyebrow tone="muted">Internal</Eyebrow>
            </div>
          </div>
        </Section>

        <Section title="Pill" description="Compact rounded chips — design's .pill recipe.">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <Pill variant="primary">Primary</Pill>
              <Pill variant="neutral">Neutral</Pill>
              <Pill variant="success">Active</Pill>
              <Pill variant="warning">Pending</Pill>
              <Pill variant="error">Blocked</Pill>
              <Pill variant="cyan">Cyan</Pill>
              <Pill variant="brand">Coach</Pill>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Pill variant="success" dot>
                Active
              </Pill>
              <Pill variant="warning" dot>
                Pending
              </Pill>
              <Pill variant="error" dot>
                Blocked
              </Pill>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Pill variant="primary" leadingIcon={FiTrendingUp}>
                Try 42.5 lb +2.5
              </Pill>
              <Pill variant="success" leadingIcon={FiCheck}>
                Applied
              </Pill>
              <Pill variant="warning" leadingIcon={FiAlertTriangle}>
                Ease in at 37.5 lb
              </Pill>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Pill variant="neutral" dismissible onDismiss={() => {}}>
                Filter: Active
              </Pill>
              <Pill
                variant="primary"
                interactive
                leadingIcon={FiTarget}
                onClick={() => {}}
              >
                Tappable
              </Pill>
            </div>
          </div>
          <Eyebrow className="mt-6 block">Raw class form (.pill.success.dot)</Eyebrow>
          <div className="flex flex-wrap gap-2 items-center mt-2">
            <span className="pill primary">Primary</span>
            <span className="pill success dot">Active</span>
            <span className="pill warning dot">Pending</span>
            <span className="pill error dot">Blocked</span>
            <span className="pill cyan">Cyan</span>
            <span className="pill neutral">Neutral</span>
            <span className="pill outline">Outline</span>
          </div>
        </Section>

        <Section title="Stat / delta" description="The design's .stat-num and .delta recipes.">
          <Card padding="md">
            <Eyebrow>Total volume</Eyebrow>
            <div className="stat-num mt-1">
              4,200 <span className="unit">lb</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span className="delta up">+8.2% vs last week</span>
              <span className="delta neg">-1 workout</span>
            </div>
          </Card>
        </Section>

        <Section title="Alert" description="Inline message box, dismissible.">
          <div className="space-y-3">
            {showInfo && (
              <Alert
                variant="info"
                title="Heads up"
                dismissible
                onDismiss={() => setShowInfo(false)}
              >
                Dismissible info alert with a title.
              </Alert>
            )}
            <Alert variant="success">All good — no title needed.</Alert>
            <Alert variant="warning" title="Heavy lift">
              You're about to do something with consequences.
            </Alert>
            <Alert variant="error" title="Something went wrong">
              The server returned an error. Please try again.
            </Alert>
          </div>
        </Section>

        <Section title="Icon" description="Wrapper around react-icons/fi with size + tone tokens.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DemoIcon as={FiHeart} tone="error" label="error" />
            <DemoIcon as={FiCheck} tone="success" label="success" />
            <DemoIcon as={FiAlertTriangle} tone="warning" label="warning" />
            <DemoIcon as={FiZap} tone="primary" label="primary" />
            <DemoIcon as={FiAward} tone="secondary" label="secondary" />
            <DemoIcon as={FiClock} tone="muted" label="muted" />
            <DemoIcon as={FiUser} tone="default" label="default" />
            <DemoIcon as={FiTrendingUp} tone="subtle" label="subtle" />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Icon as={FiHeart} size="xs" tone="error" />
            <Icon as={FiHeart} size="sm" tone="error" />
            <Icon as={FiHeart} size="md" tone="error" />
            <Icon as={FiHeart} size="lg" tone="error" />
            <Icon as={FiHeart} size="xl" tone="error" />
            <Icon as={FiHeart} size="2xl" tone="error" />
            <span className="text-sm text-fg-muted">xs · sm · md · lg · xl · 2xl</span>
          </div>
        </Section>

        <Section title="Input" description="Single-line, with icons, error, multi-line.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Plain text input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <Input
              placeholder="With leading icon"
              leadingIcon={<FiSearch className="w-4 h-4" />}
            />
            <Input placeholder="Error state" error />
            <Input placeholder="Disabled" disabled />
            <Input
              as="textarea"
              placeholder="Multi-line"
              rows={3}
              className="md:col-span-2"
            />
          </div>
        </Section>

        <Section title="Modal" description="Themed wrapper around antd v3 Modal.">
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setModalOpen(true)}>Open modal</Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
            >
              Open confirm modal
            </Button>
          </div>
          <Modal
            visible={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Edit profile"
            description="Update your business details. Changes save when you submit."
            footer={
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setModalOpen(false)}>Save</Button>
              </div>
            }
          >
            <div className="space-y-3">
              <Input placeholder="Business name" />
              <Input as="textarea" placeholder="Bio" rows={3} />
            </div>
          </Modal>
          <Modal
            visible={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            title="Delete this assessment?"
            description="This can't be undone. Clients with this assignment will need a new one."
            footer={
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setConfirmOpen(false)}
                >
                  Keep
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setConfirmOpen(false)}
                >
                  Delete
                </Button>
              </div>
            }
          />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="mt-10">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-fg">{title}</h2>
        {description ? (
          <p className="text-sm text-fg-muted mt-0.5">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function DemoIcon({ as, tone, label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 p-3 rounded-md bg-elev border border-border-soft">
      <Icon as={as} tone={tone} size="lg" />
      <span className="text-[11px] text-fg-muted">{label}</span>
    </div>
  );
}
